import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import TeamPinPromptDialog from "./TeamPinPromptDialog";
import { deDateMediumTime } from "../../lib/dateFormat";

/**
 * Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Abfrage bei Concierge":
 * Sektion im /team/profile-Bereich, sichtbar für sich selbst (nicht in
 * Admin-Impersonation eines fremden Concierges).
 *
 * Zeigt den aktuellen Status (aktiv seit / noch nicht eingerichtet) und
 * stellt drei Aktionen bereit:
 *   • PIN einrichten (nur wenn noch keine existiert)
 *   • PIN ändern (aktuelle PIN + neue PIN)
 *   • PIN vergessen — schickt 8-stelligen OTP an Team-E-Mail und öffnet
 *     den Reset-Modus (Reset-Code + neue PIN).
 */
export default function TeamPinSection() {
  const [status, setStatus] = useState({ is_set: false, updated_at: null });
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState(null);
  const [requestingReset, setRequestingReset] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/team-pin/status");
      setStatus({ is_set: !!data.is_set, updated_at: data.updated_at || null });
    } catch (e) { /* silent — team-only endpoint */ }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const startForgot = async () => {
    setRequestingReset(true);
    try {
      await api.post("/me/team-pin/request-reset");
      toast.success("Reset-Code an Ihre E-Mail gesendet.");
      setMode("reset");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRequestingReset(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return deDateMediumTime(iso);
    } catch { return iso; }
  };

  return (
    <section
      className="bg-white border border-navy/10 p-6 md:p-8"
      data-testid="team-pin-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 mb-2">
            <ShieldCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <div className="label-caps">Persönliche Signatur</div>
          </div>
          <h2 className="font-display text-2xl text-navy" data-testid="team-pin-title">
            Ihre persönliche Team-PIN
          </h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 max-w-xl leading-relaxed">
            Ihre 4-stellige PIN bestätigt jede Änderung, die Sie an einer
            Buchung speichern (Checkliste, Datum, Services). Sie ersetzt
            die frühere zentrale Admin-PIN für Buchungs-Aktionen — jeder
            Team-User arbeitet mit seiner eigenen PIN. Die Admin-PIN bleibt
            für Rollen-Switcher und Kundenprofil-Editor erhalten.
          </p>
        </div>
        <div
          className="border px-3 py-1.5 text-[10.5px] tracking-[0.18em] uppercase"
          style={{
            borderColor: status.is_set ? `${BRAND_COLORS.greenDeep}55` : `${BRAND_COLORS.gold}55`,
            color: status.is_set ? BRAND_COLORS.greenDeep : BRAND_COLORS.gold,
          }}
          data-testid={`team-pin-status-${status.is_set ? "set" : "unset"}`}
        >
          {status.is_set ? "Aktiv" : "Noch nicht eingerichtet"}
        </div>
      </div>

      {loaded && status.is_set && status.updated_at && (
        <p className="text-[12px] text-navy-dark/55 mt-3 italic">
          Zuletzt aktualisiert: {fmtDate(status.updated_at)}
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {!status.is_set && (
          <button
            type="button"
            data-testid="team-pin-setup-btn"
            onClick={() => setMode("setup")}
            className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity"
            style={{ background: BRAND_COLORS.navy }}
          >
            <KeyRound className="w-4 h-4" strokeWidth={1.6} />
            PIN einrichten
          </button>
        )}
        {status.is_set && (
          <>
            <button
              type="button"
              data-testid="team-pin-change-btn"
              onClick={() => setMode("change")}
              className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity"
              style={{ background: BRAND_COLORS.navy }}
            >
              <KeyRound className="w-4 h-4" strokeWidth={1.6} />
              PIN ändern
            </button>
            <button
              type="button"
              data-testid="team-pin-forgot-btn"
              onClick={startForgot}
              disabled={requestingReset}
              className="inline-flex items-center gap-2 border border-navy/25 text-navy px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-navy/5 transition-colors disabled:opacity-50"
            >
              {requestingReset ? "Code wird gesendet…" : "PIN vergessen"}
            </button>
          </>
        )}
      </div>

      <TeamPinPromptDialog
        open={!!mode}
        mode={mode || "setup"}
        onClose={() => setMode(null)}
        onSuccess={() => {
          setMode(null);
          load();
        }}
      />
    </section>
  );
}
